import { AMSControlAction } from "./printerResponse";
import { z } from "zod";

// Light Control Schemas
const lightNodeSchema = z.enum(["chamber_light", "work_light"]);
const lightModeSchema = z.enum(["on", "off", "flashing"]);
const lightOptionsSchema = z.object({
  onTime: z.number().optional(),
  offTime: z.number().optional(),
  loops: z.number().optional(),
  interval: z.number().optional(),
}).optional();

// Fan Control Schemas
const fanTypeSchema = z.enum(["1", "2", "3"]).transform(val => parseInt(val));
const fanSpeedSchema = z.number().min(0).max(100);

// Speed Control Schemas
const speedLevelSchema = z.enum(["1", "2", "3", "4"]);

// Temperature Control Schemas
const tempPartSchema = z.enum(["bed", "nozzle", "chamber"]);
const temperatureSchema = z.number().min(0).max(300);

// State Control Schemas
const stateSchema = z.enum(["pause", "resume", "stop"]);

// G-code Schema
const gcodesSchema = z.array(z.string());

// AMS Control Schema
const amsControlActionSchema = z.nativeEnum(AMSControlAction);

// Filament Settings Schema
const filamentSettingsSchema = z.object({
  ams_id: z.number(),
  tray_id: z.number(),
  filament_id: z.string(),
  setting_id: z.string(),
  color: z.string(),
  filament_type: z.string(),
  nozzle_temp_min: z.number(),
  nozzle_temp_max: z.number(),
});

// Filament Loading Schema
const loadFilamentSchema = z.object({
  trayId: z.number(),
  oldTemp: z.number().optional(),
  newTemp: z.number().optional(),
});

const unloadFilamentSchema = z.object({
  oldTemp: z.number().optional(),
  newTemp: z.number().optional(),
});

// Axis Control Schema
const axisSchema = z.enum(["X", "Y", "Z", "E"]);
const axisControlSchema = z.object({
  axis: axisSchema,
  distance: z.number(),
  unit: z.number().optional(),
  speed: z.number().optional(),
});

// Clean Print Error Schema
const cleanPrintErrorSchema = z.object({
  subtaskId: z.string(),
  printError: z.number(),
});

// Power Loss Recovery Schema
const powerLossRecoverySchema = z.object({
  action: z.enum(["resume", "stop"]),
  subtaskId: z.string(),
  printError: z.number(),
});

// Camera Control Schema
const cameraOperationSchema = z.enum(["ipcam_record_set", "ipcam_timelapse", "ipcam_resolution_set"]);
const cameraSchema = z.object({
  operation: cameraOperationSchema,
  value: z.union([z.boolean(), z.string()]),
});

// HMS & System Command Schemas
const hmsCommandSchema = z.object({
  errorCode: z.string(),
  jobId: z.string(),
});

// AMS Command Schemas
const amsUserSettingsSchema = z.object({
  amsId: z.number(),
  startupReadOption: z.boolean(),
  trayReadOption: z.boolean(),
  calibrateRemainFlag: z.boolean(),
});

const amsCalibrateSchema = z.object({
  amsId: z.number(),
});

const amsRefreshRfidSchema = z.object({
  trayId: z.string(),
});

const amsRefreshRfid2Schema = z.object({
  amsId: z.number(),
  slotId: z.number(),
});

const amsSelectTraySchema = z.object({
  trayId: z.string(),
});

// Other Command Schemas
const setNozzleTempNewSchema = z.object({
  extruderIndex: z.number(),
  targetTemp: temperatureSchema,
});

const printingOptionsSchema = z.object({
  autoRecovery: z.boolean(),
});

// Test light sequence schema
const testLightSequenceSchema = z.object({
  sequence: z.tuple([z.boolean(), z.boolean(), z.boolean(), z.boolean(), z.boolean()]),
  chamberLight: z.boolean(),
  workLight: z.boolean(),
  loops: z.number().int().min(1).max(10).optional(),
});

// Command Parameter Schemas
const commandSchemas = {
  updateLight: z.object({
    light: lightNodeSchema,
    mode: lightModeSchema,
    loopOptions: lightOptionsSchema,
  }),
  updateFan: z.object({
    fan: fanTypeSchema,
    speed: fanSpeedSchema,
  }),
  updateSpeed: z.object({
    speed: speedLevelSchema,
  }),
  updateTemp: z.object({
    part: tempPartSchema,
    temperature: temperatureSchema,
  }),
  updateState: z.object({
    state: stateSchema,
  }),
  gcode: z.object({
    gcodes: gcodesSchema,
  }),
  resume: z.object({
    cleanPrintError: z.boolean().optional(),
    printError: z.number().optional(),
    subtaskId: z.string().optional(),
  }),
  ams_control: z.object({
    param: z.any() // This can be refined further based on AMSControlAction structure
  }),
  refreshPrinterState: z.object({}),
  ams_filament_settings: filamentSettingsSchema,
  loadFilament: loadFilamentSchema,
  unloadFilament: unloadFilamentSchema,
  axisControl: axisControlSchema,
  autoLevel: z.object({}),
  goHome: z.object({}),
  cleanPrintError: cleanPrintErrorSchema,
  powerLossRecovery: powerLossRecoverySchema,
  camera: cameraSchema,
  taskAbort: z.object({}),
  taskCancel: z.object({
    jobId: z.string(),
  }),
  // High-priority HMS & system commands
  hmsResume: hmsCommandSchema,
  hmsIgnore: hmsCommandSchema,
  hmsStop: hmsCommandSchema,
  stopBuzzer: z.object({}),
  getAccessCode: z.object({}),
  // Medium-priority AMS commands
  amsUserSettings: amsUserSettingsSchema,
  amsCalibrate: amsCalibrateSchema,
  amsRefreshRfid: amsRefreshRfidSchema,
  amsRefreshRfid2: amsRefreshRfid2Schema,
  amsSelectTray: amsSelectTraySchema,
  amsDryingStop: z.object({}),
  // Medium-priority other commands
  setNozzleTempNew: setNozzleTempNewSchema,
  xyzAbsoluteMode: z.object({}),
  printingOptions: printingOptionsSchema,
  // Light testing command
  testLightSequence: testLightSequenceSchema,
};

export type CommandSchemas = typeof commandSchemas;

export function validateCommandParams<T extends keyof CommandSchemas>(
  command: T,
  params: unknown
): { success: true; data: z.infer<CommandSchemas[T]> } | { success: false; error: z.ZodError } {
  const schema = commandSchemas[command];
  const result = schema.safeParse(params);
  
  if (result.success) {
    return { success: true, data: result.data };
  } else {
    return { success: false, error: result.error };
  }
}

export { commandSchemas }; 